import { lastValueFrom, Observable } from 'rxjs';
import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment as config } from '../../../../environments/environment';
import { RecordsResponse } from '../../shared/models/models';
import { AudienceConfig, AudienceSize, AudienceStats, AudienceStatusRequest, UsedAudience } from '../../shared/models/audiences.models';
import { Utils } from '../../shared/classes/utils';


@Injectable({
  providedIn: 'root'
})
export class AudiencesService {
  private http = inject(HttpClient);

  getAudiences(): Observable<RecordsResponse<AudienceConfig>> {
    return this.http.get<RecordsResponse<AudienceConfig>>(`${config.adminBaseUrl}/api/audiences`);
  }

  getSingleAudience(id: number): Observable<AudienceConfig> {
    return this.http.get<AudienceConfig>(`${config.adminBaseUrl}/api/audiences/${id}/config`);
  }

  getAudienceStats(sizes: AudienceSize[], roundVisitsPercentage: boolean = true): AudienceStats {
    const members = sizes.map(a => a.count).reduce((a, b) => a + b) || 0;
    const engaged = sizes.map(a => a.via_content_count).reduce((a, b) => a + b) || 0;
    const allVisitors = sizes.map(a => a.all_visitors_count).reduce((a, b) => a + b);
    const visitsPercentage = (100 * members / allVisitors) || 0;

    return {
      members,
      engagedPercentage: Math.round(100 * engaged / members) || 0,
      visitsPercentage: roundVisitsPercentage ? Math.round(visitsPercentage) : Math.round(visitsPercentage * 100) / 100
    };
  }

  async getUsed(): Promise<UsedAudience[]> {
    return lastValueFrom(this.http.get<UsedAudience[]>(`${config.adminBaseUrl}/api/audiences/used`));
  }

  isAudienceNameExists(name: string, excludeId?: number): Observable<boolean> {
    const excludeParam = excludeId ? `&excludeId=${excludeId}` : '';
    return this.http.get<boolean>(`${config.adminBaseUrl}/api/audiences/exist?name=${encodeURIComponent(name)}${excludeParam}`);
  }

  public async addAudience(request: AudienceConfig): Promise<number> {
    const cleanAudience = this.clearAudienceConfigDomain(request);
    return await lastValueFrom(this.http.post<number>(`${config.adminBaseUrl}/api/audiences`, cleanAudience));
  }

  public async updateAudience(id: number, request: AudienceConfig): Promise<void> {
    const cleanAudience = this.clearAudienceConfigDomain(request);
    return await lastValueFrom(this.http.patch<void>(`${config.adminBaseUrl}/api/audiences/${id}`, cleanAudience));
  }

  public async changeAudienceStatus(id: number, request: AudienceStatusRequest): Promise<void> {
    await lastValueFrom(this.http.patch<void>(`${config.adminBaseUrl}/api/audiences/${id}/change_status`, request));
  }

  public async duplicateAudience(id: number): Promise<number> {
    return await lastValueFrom(this.http.post<number>(`${config.adminBaseUrl}/api/audiences/${id}/duplicate`, null));
  }

  private clearAudienceConfigDomain<T extends AudienceConfig>(audience: T): T {
    if (!audience?.config?.rulesGroups) {
      return audience;
    }
    const rulesGroups = Utils.clearRulesGroupsDomain(audience.config.rulesGroups);

    return {
      ...audience,
      config: {
        ...audience.config,
        rulesGroups
      }
    }
  }
}
